import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { useStore } from '@/shared/store/store-context';

import { useCreateDraftOrder } from '../hooks/use-draft-order-mutations';

/**
 * Starts an empty draft right away and replaces this URL with its editor, so every field is
 * saved against a real draft from the first change and the back button skips this page.
 */
function NewDraftOrder() {
  const { t } = useTranslation('draftOrders');
  const store = useStore();
  const navigate = useNavigate();
  const create = useCreateDraftOrder(store.id);
  // StrictMode runs mount effects twice; one POST is enough.
  const started = useRef(false);

  const start = useCallback(() => {
    create.mutate(
      { lines: [] },
      {
        onSuccess: (draft) =>
          void navigate({
            to: '/s/$storeId/draft-orders/$draftId',
            params: { storeId: store.id, draftId: draft.id },
            replace: true,
          }),
      },
    );
  }, [create, navigate, store.id]);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    start();
  }, [start]);

  return (
    <PageContent>
      <PageHeader
        title={t('new')}
        backTo={{ to: '/s/$storeId/draft-orders', params: { storeId: store.id } }}
        backLabel={t('title')}
      />
      {create.isError ? (
        <ErrorState error={create.error} onRetry={start} />
      ) : (
        <div className="flex flex-col gap-3" aria-busy="true">
          <p className="text-muted-foreground text-sm">{t('creating')}</p>
          <Skeleton className="h-40" />
          <Skeleton className="h-24" />
        </div>
      )}
    </PageContent>
  );
}

export function NewDraftOrderPage() {
  return (
    <FeatureErrorBoundary name="draftOrders.new">
      <NewDraftOrder />
    </FeatureErrorBoundary>
  );
}
